import type {
  Control,
  FieldPath,
  FieldValues,
  RegisterOptions,
} from 'react-hook-form';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/shared/shadcn/ui/form';

export interface FieldConfig {
  visible?: boolean;
  required?: boolean;
  fieldId?: string;
}

interface ConfigurableTextareaFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  config: FieldConfig | undefined;
  placeholder: string;
  label?: string;
  rows?: number;
  customRules?: RegisterOptions<TFieldValues>;
  className?: string;
}

export function ConfigurableTextareaField<TFieldValues extends FieldValues>({
  control,
  name,
  config,
  placeholder,
  label,
  rows = 2,
  customRules = {},
  className = 'w-full rounded-lg border px-3 py-2 text-gray-700 focus:border-blue-500 focus:outline-none',
}: ConfigurableTextareaFieldProps<TFieldValues>) {
  if (!config?.visible) {
    return null;
  }

  const rules: RegisterOptions<TFieldValues> = {
    ...customRules,
  };

  // Add required rule if specified in config
  if (config.required && !customRules.required) {
    rules.required = `${placeholder} information is required`;
  }

  const displayPlaceholder = `${placeholder}${config.required ? ' *' : ''}`;

  return (
    <FormField
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <FormItem className='mb-4'>
          {label && (
            <FormLabel>
              {label}
              {config.required ? ' *' : ''}
            </FormLabel>
          )}
          <FormControl>
            <textarea
              className={className}
              rows={rows}
              placeholder={displayPlaceholder}
              {...field}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
